import React, { useEffect, useState } from 'react';
import { useNotification } from '../../../../components/framework/NotificationContext';
import { getAnalyticsSummary } from '../../../../services/emailCampaignService';

const TONE_COLORS = {
    navy: { bg: '#f0f4ff', fg: '#003366' },
    success: { bg: '#e6f6ec', fg: '#1e7a3f' },
    danger: { bg: '#fdeceb', fg: '#c0392b' },
};

const StatTile = ({ label, value, icon, tone = 'navy' }) => {
    const { bg, fg } = TONE_COLORS[tone];
    return (
        <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="admin-card h-100 email-stat-tile">
                <div className="email-stat-icon" style={{ backgroundColor: bg, color: fg }}>
                    <i className={`bi ${icon}`}></i>
                </div>
                <div className="email-stat-value">{value ?? '—'}</div>
                <div className="email-stat-label">{label}</div>
            </div>
        </div>
    );
};

/**
 * Phase 1 analytics: plain stat tiles only — no chart library wired yet.
 * Future chart work belongs in a later phase per the approved plan.
 */
const AnalyticsPanel = () => {
    const { error: notifyError } = useNotification();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getAnalyticsSummary()
            .then(setSummary)
            .catch((err) => notifyError(err.message || 'Failed to load analytics'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>;
    }

    return (
        <div>
            <div className="row">
                <StatTile label="Sent Today" value={summary?.sentToday} icon="bi-calendar-day" />
                <StatTile label="Sent This Week" value={summary?.sentThisWeek} icon="bi-calendar-week" />
                <StatTile label="Sent This Month" value={summary?.sentThisMonth} icon="bi-calendar-month" />
                <StatTile label="Success Rate" value={summary?.successRate != null ? `${summary.successRate}%` : null} icon="bi-check-circle" tone="success" />
                <StatTile label="Failure Rate" value={summary?.failureRate != null ? `${summary.failureRate}%` : null} icon="bi-x-circle" tone="danger" />
                <StatTile label="Avg Recipients / Campaign" value={summary?.avgRecipientsPerCampaign} icon="bi-people" />
            </div>

            <div className="admin-card">
                <h6 className="mb-3"><i className="bi bi-file-earmark-text me-2"></i>Most Used Templates</h6>
                {(summary?.mostUsedTemplates || []).length === 0 ? (
                    <p className="text-muted text-center py-3 mb-0">No data yet.</p>
                ) : (
                    <ul className="list-group list-group-flush">
                        {summary.mostUsedTemplates.map((t) => (
                            <li key={t.templateId || t.name} className="list-group-item d-flex justify-content-between">
                                <span>{t.name}</span>
                                <span className="badge bg-light text-dark border">{t.usageCount} sends</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="admin-card mt-3">
                <h6 className="mb-3"><i className="bi bi-person-badge me-2"></i>Top Administrators by Email Activity</h6>
                {(summary?.topAdmins || []).length === 0 ? (
                    <p className="text-muted text-center py-3 mb-0">No data yet.</p>
                ) : (
                    <ul className="list-group list-group-flush">
                        {summary.topAdmins.map((a) => (
                            <li key={a.adminId || a.name} className="list-group-item d-flex justify-content-between">
                                <span>{a.name}</span>
                                <span className="badge bg-light text-dark border">{a.campaignCount} campaigns</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default AnalyticsPanel;
